import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { createValidationMessages, toaster, useAppForm } from '@/shared/libs';
import {
  Button,
  Dialog,
  DialogFooter,
  FormField,
  FormInput,
  Label,
} from '@/shared/ui';
import { FieldError } from '@/shared/ui/form';
import sdk from '@psp-aggregator/sdk';
import { useMutation } from '@tanstack/react-query';

const createChangePasswordSchema = (
  t: ReturnType<typeof useTranslation>['t'],
) => {
  const messages = createValidationMessages(t);

  return z
    .object({
      confirmPassword: z.string().min(1, { message: messages.required() }),
      newPassword: z.string().min(8, { message: messages.minLength(8) }),
      oldPassword: z.string().min(1, { message: messages.required() }),
    })
    .refine((values) => values.newPassword === values.confirmPassword, {
      message: t('validation.passwordMismatch'),
      path: ['confirmPassword'],
    });
};

type ChangePasswordValues = z.infer<
  ReturnType<typeof createChangePasswordSchema>
>;

const DEFAULT_VALUES: ChangePasswordValues = {
  confirmPassword: '',
  newPassword: '',
  oldPassword: '',
};

export interface ChangePasswordModalProps {
  onClose: () => void;
}

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const schema = useMemo(() => createChangePasswordSchema(t), [t]);
  const form = useAppForm(schema, { defaultValues: DEFAULT_VALUES });
  const mutation = useMutation({
    mutationFn: (payload: { new_password: string; old_password: string }) =>
      sdk.requests.changePassword(payload),
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({
        new_password: values.newPassword,
        old_password: values.oldPassword,
      });
      toaster.success({ title: t('toast.passwordChanged') });
      onClose();
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.unknown'))
          : t('errors.unknown'),
      });
    }
  });

  return (
    <Dialog
      title={t('pages.changePassword.title')}
      width={420}
      onClose={onClose}>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <FormField
          control={form.control}
          name="oldPassword"
          render={({ error, field }) => (
            <div className="space-y-2">
              <Label htmlFor="old-password">
                {t('pages.changePassword.oldPassword')}
              </Label>
              <FormInput {...field} id="old-password" type="password" />
              <FieldError error={error} />
            </div>
          )}
        />
        <FormField
          control={form.control}
          name="newPassword"
          render={({ error, field }) => (
            <div className="space-y-2">
              <Label htmlFor="new-password">
                {t('pages.changePassword.newPassword')}
              </Label>
              <FormInput {...field} id="new-password" type="password" />
              <FieldError error={error} />
            </div>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ error, field }) => (
            <div className="space-y-2">
              <Label htmlFor="confirm-password">
                {t('pages.changePassword.confirmPassword')}
              </Label>
              <FormInput {...field} id="confirm-password" type="password" />
              <FieldError error={error} />
            </div>
          )}
        />
        <DialogFooter
          endSlot={
            <>
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('buttons.cancel')}
              </Button>
              <Button
                disabled={mutation.isPending}
                type="submit"
                variant="brand">
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('buttons.save')
                )}
              </Button>
            </>
          }
        />
      </form>
    </Dialog>
  );
};

ChangePasswordModal.displayName = 'ChangePasswordModal';

export const useChangePasswordModalState = (): {
  open: boolean;
  setOpen: (open: boolean) => void;
} => {
  const [open, setOpen] = useState(false);

  return { open, setOpen };
};
